import { SearchOutlined } from '@ant-design/icons';
import { Drawer, Input, Select, Spin } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import { montserrat } from '@/components/Layout';

interface OptionType {
  lable: number; // typo matched to existing codebase
  value: string;
}

interface ResponsiveSelectProps {
  value?: any;
  onChange?: (value: any, option: OptionType) => void;
  options: OptionType[];
  placeholder?: string;
  loading?: boolean;
  prefixCls?: string;
  size?: 'large' | 'middle' | 'small';
  variant?: 'outlined' | 'borderless' | 'filled';
  className?: string;
  styles?: any;
  disabled?: boolean;
}

const ResponsiveSelect: React.FC<ResponsiveSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  loading,
  prefixCls,
  size,
  variant,
  className,
  styles,
  disabled,
}) => {
  const twScreen = useTailwindBreakpoint();
  const [modalVisible, setModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const inputRef = useRef<any>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (modalVisible) {
      // Focus the input when the drawer opens
      // Using a short timeout to ensure the drawer animation has started/completed
      // and the element is ready to receive focus.
      timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [modalVisible]);

  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    return options.filter((opt) =>
      opt.value.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [options, searchText]);

  const handleOptionClick = (option: OptionType) => {
    onChange?.(option.value, option);
    setModalVisible(false);
    setSearchText('');
  };

  if (twScreen.lg) {
    return (
      <Select
        value={value}
        prefixCls={prefixCls}
        size={size}
        variant={variant}
        className={className}
        styles={styles}
        onChange={(v, o) => onChange?.(v, o as OptionType)}
        loading={loading}
        showSearch={true}
        options={options.map((opt) => ({
          ...opt,
          label: opt.value, // AntD requires 'label'
        }))}
        placeholder={placeholder}
        optionFilterProp='label'
        disabled={disabled}
      />
    );
  }

  return (
    <>
      <div
        className={twMerge('relative w-full', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}
        onClick={() => !disabled && setModalVisible(true)}
      >
        <Select
          value={value}
          prefixCls={prefixCls}
          size={size}
          variant={variant}
          className={twMerge(className, 'w-full')}
          styles={styles}
          loading={loading}
          open={false}
          showSearch={false}
          options={options.map((opt) => ({
            ...opt,
            label: opt.value,
          }))}
          placeholder={placeholder}
          disabled={disabled}
          style={{ pointerEvents: 'none' }}
        />
        <div className='absolute inset-0 z-10' />
      </div>

      <Drawer
        title={<span className='text-white font-medium'>{placeholder}</span>}
        placement='right'
        width='100%'
        closable={true}
        onClose={() => setModalVisible(false)}
        open={modalVisible}
        className='bg-grayZl-950'
        styles={{
          header: { borderBottom: '1px solid #27272a', padding: '16px' },
          body: { padding: 0, display: 'flex', flexDirection: 'column' },
        }}
        style={{
          fontFamily: montserrat.style.fontFamily,
        }}
        closeIcon={<span className='text-gray-400'>✕</span>}
      >
        <div className='p-4 border-b border-grayZl-800'>
          <Input
            ref={inputRef}
            prefix={<SearchOutlined className='text-gray-500' />}
            placeholder='Search...'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size='large'
            className='placeholder:text-grayZl-700 bg-transparent text-white border-grayZl-800'
            autoFocus
          />
        </div>
        <div className='flex-1 overflow-y-auto'>
          {loading ? (
            <div className='flex items-center justify-center p-8'>
              <Spin size='large' />
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <div
                key={opt.lable}
                className='p-4 border-b border-grayZl-800 text-gray-200 hover:bg-grayZl-925 active:bg-grayZl-925 cursor-pointer transition-colors'
                onClick={() => handleOptionClick(opt)}
              >
                {opt.value}
              </div>
            ))
          ) : (
            <div className='p-4 text-center text-gray-500'>
              No results found
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default ResponsiveSelect;
